import React, { useState, useEffect, useCallback, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineBoard, DineOutletItem } from 'api/types';

/**
 * "Put dishes on the board" — the outlet's card, searchable, many at a time
 * (spec 8.8: "The board is set for a service, not a dish at a time").
 *
 * Only dishes THIS outlet sells (Outlet Items) are offered: the waiter's card carries nothing
 * else, so a special for any other dish would never be seen (2026-10-29). The search runs on the
 * server and every page is read, so a long menu is never cut at the API's 100-row page.
 *
 * Every chosen dish gets the same run of dates and the same line. A dish already booked is named
 * before saving; saving REPLACES its run rather than adding a second, so a dish is never on the
 * board twice.
 */
interface Props {
  isOpen: boolean;
  businessDate: string;
  /** Dish ids with a run that is on or booked — shown as "already booked". */
  alreadyOn: string[];
  onSaved: (board: DineBoard) => void;
  onClose: () => void;
}

/** The API's page cap; the list reads page after page up to MAX_PAGES. */
const PAGE_SIZE = 100;
const MAX_PAGES = 20;

/** Every dish (not size) the outlet lists, matching the search, read page by page. */
async function loadOutletDishes(searchTerm: string): Promise<DineOutletItem[]> {
  const all: DineOutletItem[] = [];
  for (let page = 1; page <= MAX_PAGES; page++) {
    const r = await dineOpsService.getOutletItems({ page, pageSize: PAGE_SIZE, show: 'listed', searchTerm: searchTerm || undefined });
    all.push(...(r.data ?? []));
    if (!r.pagination?.hasNextPage) break;
  }
  // A size is listed with its dish; the board takes the dish.
  return all.filter((i) => !i.parentItemId);
}

const DineOpsSpecialAddModal: React.FC<Props> = ({ isOpen, businessDate, alreadyOn, onSaved, onClose }) => {
  const [items, setItems] = useState<DineOutletItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [q, setQ] = useState('');
  /** Chosen dishes by id, kept across searches so a pick made under one search is not lost. */
  const [chosen, setChosen] = useState<Map<string, DineOutletItem>>(new Map());
  const [from, setFrom] = useState(businessDate);
  const [to, setTo] = useState(businessDate);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The latest search wins; an answer to an older one is dropped. */
  const requestRef = useRef(0);

  useEffect(() => {
    if (!isOpen) return;
    setChosen(new Map()); setQ(''); setNote(''); setError(null);
    setFrom(businessDate); setTo(businessDate);
  }, [isOpen, businessDate]);

  const load = useCallback(async (term: string) => {
    const mine = ++requestRef.current;
    setLoading(true); setLoadError(null);
    try {
      const list = await loadOutletDishes(term.trim());
      if (mine === requestRef.current) setItems(list);
    } catch (err: any) {
      if (mine === requestRef.current) {
        setItems([]);
        setLoadError(err?.response?.data?.message ?? err?.message ?? 'The dishes could not be loaded.');
      }
    } finally {
      if (mine === requestRef.current) setLoading(false);
    }
  }, []);

  // Search on the server, a moment after typing stops.
  useEffect(() => {
    if (!isOpen) return undefined;
    const t = window.setTimeout(() => void load(q), q ? 300 : 0);
    return () => window.clearTimeout(t);
  }, [isOpen, q, load]);

  const toggle = useCallback((item: DineOutletItem) =>
    setChosen((p) => {
      const next = new Map(p);
      if (next.has(item.itemId)) next.delete(item.itemId); else next.set(item.itemId, item);
      return next;
    }), []);

  const allVisibleChosen = items.length > 0 && items.every((i) => chosen.has(i.itemId));

  const save = useCallback(async () => {
    if (chosen.size === 0) { setError('Pick at least one dish.'); return; }
    if (!from || !to) { setError('Both dates are needed.'); return; }
    if (to < from) { setError('The last day cannot be before the first.'); return; }
    setSaving(true); setError(null);
    try {
      const ids = Array.from(chosen.keys());
      const board = await dineOpsService.putManyOnBoard({ itemIds: ids, fromDate: from, toDate: to, note: note.trim() || undefined });
      const rebooked = ids.filter((id) => alreadyOn.includes(id)).length;
      notificationService.success('On the board', `${ids.length} dish(es) put on${rebooked ? `, ${rebooked} re-dated` : ''}.`);
      onSaved(board);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'The board could not be saved.');
    } finally {
      setSaving(false);
    }
  }, [chosen, from, to, note, alreadyOn, onSaved]);

  if (!isOpen) return null;
  const chosenItems = Array.from(chosen.values());
  const soldOut = (i: DineOutletItem) => i.isSoldOutHere || i.isSoldOutEverywhere;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ minHeight: 'auto' }}>
        <div className="modal-header">
          <h2 className="modal-title">{chosen.size ? `Put ${chosen.size} on the board` : 'Put dishes on the board'}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}><IconSystem.x size={18} /></button>
        </div>

        <div className="modal-content" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {error && <div className="form-error-message" style={{ marginBottom: 12 }}>{error}</div>}
          <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 10 }}>
            They all get the same run of dates and the same line. Change one afterwards from its own row if it
            needs different ones. A dish already booked is re-dated, never added twice.
          </div>

          <div className="form-grid-2">
            <div className="form-group">
              <label className="form-label">On the board from</label>
              <input type="date" className="form-input" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Until</label>
              <input type="date" className="form-input" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Something to say about them</label>
            <input type="text" className="form-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Chef's pick" maxLength={200} />
          </div>

          {chosenItems.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {chosenItems.map((i) => (
                <button key={i.itemId} type="button" className="btn btn-secondary btn-sm" onClick={() => toggle(i)} title="Remove from the selection">
                  {i.itemName}{alreadyOn.includes(i.itemId) ? ' (booked — will be re-dated)' : ''} ×
                </button>
              ))}
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Dishes this outlet sells</label>
            <input type="text" className="form-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by dish, code, group or category…" autoFocus />
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 4 }}>
              Only dishes listed on Outlet Items for this outlet reach the waiters. Not here? List it there first.
            </div>
          </div>

          <div style={{ border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 6, maxHeight: 300, overflowY: 'auto' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '1px solid var(--border-color, #eee)', fontSize: 12, opacity: 0.8 }}>
              <input
                type="checkbox"
                checked={allVisibleChosen}
                onChange={(e) => setChosen((p) => {
                  const next = new Map(p);
                  items.forEach((i) => (e.target.checked ? next.set(i.itemId, i) : next.delete(i.itemId)));
                  return next;
                })}
              />
              Select all shown ({items.length}) · {chosen.size} chosen
            </label>
            {loading && <div style={{ padding: 10, opacity: 0.7 }}>Loading the dishes…</div>}
            {!loading && loadError && (
              <div className="form-error-message" role="alert" style={{ padding: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
                <span style={{ flex: 1 }}>{loadError}</span>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => void load(q)}>Try again</button>
              </div>
            )}
            {!loading && !loadError && items.length === 0 && (
              <div style={{ padding: 10, opacity: 0.7 }}>
                {q.trim() ? 'No dish this outlet sells matches.' : 'This outlet sells no dishes yet. List them on Outlet Items first.'}
              </div>
            )}
            {!loading && items.map((i) => (
              <label key={i.itemId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '1px solid var(--border-color, #f3f4f6)', cursor: 'pointer' }}>
                <input type="checkbox" checked={chosen.has(i.itemId)} onChange={() => toggle(i)} />
                <span style={{ flex: 1 }}>
                  <strong>{i.itemName}</strong> <span style={{ opacity: 0.6 }}>{i.itemCode} · {i.courseName} › {i.groupName}</span>
                </span>
                <span style={{ opacity: 0.7 }}>{i.isOpenPrice ? 'open priced' : i.effectivePrice.toFixed(2)}</span>
                {alreadyOn.includes(i.itemId) && <span style={{ fontSize: 11, opacity: 0.7 }}>booked</span>}
                {soldOut(i) && <span className="text-danger" style={{ fontSize: 11 }}>sold out</span>}
              </label>
            ))}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={saving || chosen.size === 0}>
            {saving ? 'Saving…' : `Put ${chosen.size || ''} on the board`}
          </button>
        </div>
      </div>
    </>
  );
};

export default DineOpsSpecialAddModal;
